import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateRichTextElement } from '../../../redux/TabTile/TabActions';
import { RichTextElement } from '../../../redux/TabTile/TabStateInterfaces';
import {
  Button,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { CustomEditor } from './SlateEditor';
import { Tooltip } from '@mui/material';
import { AlignFormat } from './SlateEditor';
import { Editor, Range as SlateRange } from 'slate';
import { Transforms, Element as SlateElement } from 'slate';
import { ReactEditor } from 'slate-react';
import ColorPickerPopover from '../ColorPickerObjects';
import SliderWithInput from '../../ChartOptions/SliderWithInput';
import MuiIcon, { MuiIconName } from './MuiIcons';
import './RichText.css';

interface Props {
  richTextElements: RichTextElement[];
  selectedRichTextId: string;
  tabId: number;
  color?: string;
  editor: Editor | null;
  editorChangeCounter?: number;
}

interface FormatButtonProps {
  tooltip: string;
  isActive: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  icon: MuiIconName;
}

const RichTextSidebarToolbar: React.FC<Props> = ({
  richTextElements,
  selectedRichTextId,
  tabId,
  editor,
  color,
  editorChangeCounter,
}) => {
  const dispatch = useDispatch();

  const [bgColorAnchor, setBgColorAnchor] = useState<HTMLElement | null>(null);
  const [textColorAnchor, setTextColorAnchor] = useState<HTMLElement | null>(null);
  const [savedSelection, setSavedSelection] = useState<any>(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  const selectedElement = richTextElements.find((el) => el.id === selectedRichTextId);

  if (!selectedElement || !editor) {
    return <div className="optionsInfo">Select a text box to edit</div>;
  }
  const isSelectionExpanded = (editor: Editor): boolean => {
    const { selection } = editor;

    return selection ? !SlateRange.isCollapsed(selection) : false;
  };

  const handleFormatChange = (format: keyof Partial<RichTextElement>, value: any) => {
    dispatch(updateRichTextElement(tabId, selectedRichTextId, { [format]: value }));
  };

  const handleAlign = (align: AlignFormat) => {
    if (editor) {
      ReactEditor.focus(editor);
      CustomEditor.toggleAlign(editor, align);
    }
  };

  const getTextColor = () => {
    return CustomEditor.getLeafColor(editor) || color || '#000000';
  };

  const FormatButton: React.FC<FormatButtonProps> = ({ tooltip, isActive, onClick, icon }) => (
    <Tooltip
      title={tooltip}
      componentsProps={{
        tooltip: {
          sx: {
            fontSize: '12px',
          },
        },
      }}
    >
      <Button variant={isActive ? 'contained' : 'outlined'} onMouseDown={onClick}>
        <MuiIcon icon={icon} />
      </Button>
    </Tooltip>
  );

  return (
    <div className="optionsInfo">
      <div className="richtext-style-buttons">
        <FormatButton
          tooltip="Bold"
          icon="bold"
          isActive={CustomEditor.isMarkActive(editor, 'bold')}
          onClick={(e: any) => {
            e.preventDefault();
            ReactEditor.focus(editor);
            CustomEditor.toggleMark(editor, 'bold');
          }}
        />
        <FormatButton
          tooltip="Italic"
          icon="italic"
          isActive={CustomEditor.isMarkActive(editor, 'italic')}
          onClick={(e: any) => {
            e.preventDefault();
            ReactEditor.focus(editor);
            CustomEditor.toggleMark(editor, 'italic');
          }}
        />
        <FormatButton
          tooltip="Underline"
          icon="underline"
          isActive={CustomEditor.isMarkActive(editor, 'underline')}
          onClick={(e: any) => {
            e.preventDefault();
            ReactEditor.focus(editor);
            CustomEditor.toggleMark(editor, 'underline');
          }}
        />
        <FormatButton
          tooltip="Strikethrough"
          icon="strikethrough"
          isActive={CustomEditor.isMarkActive(editor, 'strikethrough')}
          onClick={(e: any) => {
            e.preventDefault();
            ReactEditor.focus(editor);
            CustomEditor.toggleMark(editor, 'strikethrough');
          }}
        />
        <FormatButton
          tooltip="Superscript"
          icon="superscript"
          isActive={CustomEditor.isMarkActive(editor, 'superscript')}
          onClick={(e: any) => {
            e.preventDefault();
            ReactEditor.focus(editor);
            CustomEditor.toggleSuperscript(editor);
          }}
        />
        <FormatButton
          tooltip="Subscript"
          icon="subscript"
          isActive={CustomEditor.isMarkActive(editor, 'subscript')}
          onClick={(e: any) => {
            e.preventDefault();
            ReactEditor.focus(editor);
            CustomEditor.toggleSubscript(editor);
          }}
        />
        <FormatButton
          tooltip="Heading 1"
          icon="headingOne"
          isActive={CustomEditor.isBlockActive(editor, 'heading-one')}
          onClick={(e: any) => {
            e.preventDefault();
            ReactEditor.focus(editor);
            CustomEditor.toggleBlock(editor, 'heading-one');
          }}
        />
        <FormatButton
          tooltip="Heading 2"
          icon="headingTwo"
          isActive={CustomEditor.isBlockActive(editor, 'heading-two')}
          onClick={(e: any) => {
            e.preventDefault();
            ReactEditor.focus(editor);
            CustomEditor.toggleBlock(editor, 'heading-two');
          }}
        />
        <FormatButton
          tooltip="Align Left"
          icon="alignLeft"
          isActive={CustomEditor.isAlignActive(editor, 'left')}
          onClick={(e) => {
            e.preventDefault();
            handleAlign('left');
          }}
        />
        <FormatButton
          tooltip="Align Center"
          icon="alignCenter"
          isActive={CustomEditor.isAlignActive(editor, 'center')}
          onClick={(e: any) => {
            e.preventDefault();
            handleAlign('center');
          }}
        />
        <FormatButton
          tooltip="Align Right"
          icon="alignRight"
          isActive={CustomEditor.isAlignActive(editor, 'right')}
          onClick={(e: any) => {
            e.preventDefault();
            handleAlign('right');
          }}
        />
        <FormatButton
          tooltip="Insert Link"
          icon="link"
          isActive={CustomEditor.isLinkActive(editor)}
          onClick={(e: any) => {
            e.preventDefault();
            if (editor.selection) {
              setSavedSelection(editor.selection);
            }
            setLinkUrl('');
            setLinkDialogOpen(true);
          }}
        />
      </div>
      <div className="richtext-style-buttons" style={{ marginTop: '0.5rem' }}>
        <FormatButton
          tooltip="Numbered List"
          icon="orderedList"
          isActive={CustomEditor.isBlockActive(editor, 'ordered-list')}
          onClick={(e: any) => {
            e.preventDefault();
            ReactEditor.focus(editor);
            CustomEditor.toggleBlock(editor, 'ordered-list');
          }}
        />
        <FormatButton
          tooltip="Bulleted List"
          icon="unorderedList"
          isActive={CustomEditor.isBlockActive(editor, 'unordered-list')}
          onClick={(e: any) => {
            e.preventDefault();
            ReactEditor.focus(editor);
            CustomEditor.toggleBlock(editor, 'unordered-list');
          }}
        />
      </div>

      <div className="optionsInfo">
        <FormControl fullWidth size="small">
          <div className="optionDescription" style={{ marginBottom: 3, color: color }}>
            Font Size
          </div>
          <Select
            value={CustomEditor.getMarkValue(editor, 'fontSize') || selectedElement.fontSize}
            onChange={(e) => {
              e.preventDefault();
              ReactEditor.focus(editor);
              CustomEditor.toggleMark(editor, 'fontSize', Number(e.target.value));
            }}
            sx={{
              fontSize: 12,
              backgroundColor: 'white',
              textAlign: 'left',
              width: '100%',
              height: '1.5rem',
              color: '#404040',
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#2bb9bb',
              },
            }}
          >
            {[12, 14, 16, 18, 20, 24, 28, 32, 40].map((size) => (
              <MenuItem key={size} value={size}>
                {size}px
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div>
        <div className="optionDescription" style={{ marginTop: '1rem', color: color }}>
          Border Radius
        </div>
        <SliderWithInput
          sliderValue={selectedElement.borderRadius ?? 12}
          sliderMinMax={{ min: 0, max: 50, step: 1 }}
          changeValue={(val) => handleFormatChange('borderRadius', Math.max(0, Math.min(50, val)))}
        />
      </div>
      <div>
        <div className="optionDescription" style={{ marginTop: '1rem', color: color }}>
          Padding
        </div>
        <SliderWithInput
          sliderValue={selectedElement.padding ?? 0}
          sliderMinMax={{ min: 0, max: 50, step: 1 }}
          changeValue={(val) => handleFormatChange('padding', Math.max(0, Math.min(50, val)))}
        />
      </div>

      <div
        className="optionDescription"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          marginTop: '1rem',
          color: color,
          alignItems: 'start',
        }}
      >
        <span>Text Color</span>
        <div
          onMouseDown={(e) => {
            e.preventDefault();
            if (editor.selection) {
              setSavedSelection(editor.selection);
            }
            // Capture the element as the anchor
            setTextColorAnchor(e.currentTarget);
          }}
          style={{
            height: '1.25rem',
            width: '100px',
            backgroundColor: getTextColor(),
            border: '2px solid darkgray',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        />
      </div>

      <ColorPickerPopover
        isOpen={Boolean(textColorAnchor)}
        anchorEl={textColorAnchor}
        onClose={() => {
          setTextColorAnchor(null);
          // restore focus ONCE only
          if (savedSelection && editor) {
            Transforms.select(editor, savedSelection);
            ReactEditor.focus(editor);
          }
        }}
        currentColor={getTextColor()}
        onColorChange={(newColor) => {
          if (!editor) return;

          if (isSelectionExpanded(editor)) {
            CustomEditor.toggleMark(editor, 'color', newColor);
          } else {
            handleFormatChange('color', newColor);
            CustomEditor.toggleMark(editor, 'color', newColor);
          }
          setSavedSelection(editor.selection);
        }}
      />
      <div
        className="optionDescription"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          marginTop: '1rem',
          alignItems: 'start',
          color: color,
        }}
      >
        <span>Background Color</span>
        <div
          // Capture the element as the anchor
          onClick={(e) => setBgColorAnchor(e.currentTarget)}
          style={{
            height: '1.25rem',
            width: '100px',
            backgroundColor: selectedElement.backgroundColor || '#ffffff',
            border: '2px solid darkgray',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        />
      </div>
      <ColorPickerPopover
        isOpen={Boolean(bgColorAnchor)}
        anchorEl={bgColorAnchor}
        onClose={() => setBgColorAnchor(null)}
        currentColor={selectedElement.backgroundColor || '#ffffff'}
        onColorChange={(color) => {
          dispatch(
            updateRichTextElement(tabId, selectedRichTextId, {
              backgroundColor: color,
              isBackgroundManual: true,
            }),
          );
        }}
      />
      <Dialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 14 }}>Add Link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="URL"
            type="url"
            fullWidth
            variant="outlined"
            placeholder="https://example.com"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (linkUrl) {
                  if (savedSelection) {
                    Transforms.select(editor, savedSelection);
                  }
                  ReactEditor.focus(editor);
                  CustomEditor.insertLink(editor, linkUrl);
                  setLinkDialogOpen(false);
                  setLinkUrl('');
                }
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#2bb9bb',
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#2bb9bb',
              },
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            paddingRight: '24px',
          }}
        >
          <Button
            onClick={() => setLinkDialogOpen(false)}
            sx={{ color: '#666', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (linkUrl) {
                if (savedSelection) {
                  Transforms.select(editor, savedSelection);
                }
                ReactEditor.focus(editor);
                CustomEditor.insertLink(editor, linkUrl);
                setLinkDialogOpen(false);
                setLinkUrl('');
              }
            }}
            variant="contained"
            sx={{
              backgroundColor: '#2BB9BB',
              color: 'white',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#249d9f' },
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default RichTextSidebarToolbar;
